import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiHome, FiMail, FiBarChart2, FiTrendingUp, FiTarget, FiSettings, FiGrid, FiDollarSign, FiLink } from 'react-icons/fi';
import { ChevronDown, ChevronUp } from 'lucide-react';

export function Sidebar() {
  const location = useLocation();
  const [expandedSections, setExpandedSections] = useState({
    analytics: true,
    settings: true
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="w-56 bg-gray-50 min-h-screen border-r border-gray-200">
      {/* Logo */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
        <div className="w-8 h-8 relative">
          <div className="w-6 h-6 bg-black rounded-sm absolute top-0 left-0"></div>
          <div className="w-3 h-3 bg-black rounded-full absolute bottom-0 right-0"></div>
        </div>
        <div className="flex flex-col">
          <span className="font-semibold text-base">Subframe</span>
          <span className="text-gray-500 text-xs">subframe.com</span>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="px-2 py-2">
        <Link
          to="/onboard"
          className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
            isActive('/onboard') ? 'bg-gray-200 font-medium' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <FiHome className="text-gray-500" size={18} />
          <span>Home</span>
        </Link>
        
        <Link
          to="/inbox"
          className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
            isActive('/inbox') ? 'bg-gray-200 font-medium' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <FiMail className="text-gray-500" size={18} />
          <span>Inbox</span>
        </Link>
        
        <Link
          to="/reports"
          className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
            isActive('/reports') ? 'bg-gray-200 font-medium' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          <FiBarChart2 className="text-gray-500" size={18} />
          <span>Reports</span>
        </Link>
      </nav>

      {/* Analytics Section */}
      <div className="px-2 mt-1">
        <button 
          className="flex items-center justify-between w-full px-2 py-1.5 text-left text-gray-600 text-sm font-medium rounded-md hover:bg-gray-100"
          onClick={() => toggleSection('analytics')}
        >
          <span>Analytics</span>
          {expandedSections.analytics ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        
        {expandedSections.analytics && (
          <div className="mt-1 ml-1 space-y-0.5">
            <Link
              to="/dashboard"
              className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
                isActive('/dashboard') ? 'bg-gray-200 font-medium' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiBarChart2 className="text-gray-500" size={16} />
              <span>Dashboard</span>
            </Link>
            
            <Link
              to="/trends"
              className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
                isActive('/trends') ? 'bg-gray-200 font-medium' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiTrendingUp className="text-gray-500" size={16} />
              <span>Trends</span>
            </Link>
            
            <Link
              to="/campaigns"
              className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
                isActive('/campaigns') ? 'bg-gray-200 font-medium' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiTarget className="text-gray-500" size={16} />
              <span>Campaigns</span>
            </Link>
          </div>
        )}
      </div>

      {/* Settings Section */}
      <div className="px-2 mt-1">
        <button 
          className="flex items-center justify-between w-full px-2 py-1.5 text-left text-gray-600 text-sm font-medium rounded-md hover:bg-gray-100"
          onClick={() => toggleSection('settings')}
        >
          <span>Settings</span>
          {expandedSections.settings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        
        {expandedSections.settings && (
          <div className="mt-1 ml-1 space-y-0.5">
            <Link
              to="/company"
              className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
                isActive('/company') ? 'bg-gray-200 font-medium' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiGrid className="text-gray-500" size={16} />
              <span>Company</span>
            </Link>
            
            <Link
              to="/payments"
              className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
                isActive('/payments') ? 'bg-gray-200 font-medium' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiDollarSign className="text-gray-500" size={16} />
              <span>Payments</span>
            </Link>
            
            <Link
              to="/integrations"
              className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm ${
                isActive('/integrations') ? 'bg-gray-200 font-medium' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <FiLink className="text-gray-500" size={16} />
              <span>Integrations</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
} 